"use client";

import { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { StatementHistoryItem } from "lib/api-types/schemas";
import { useTenantRouteContext } from "@/components/app-shell/TenantRouteContext";
import { SIGNALR_EVENTS } from "@/lib/signalr/events";
import type { PatientArChangedEvent } from "@/lib/signalr/types";
import { useSignalREvent } from "@/lib/signalr/useSignalREvent";
import { statementHistoryQueryKey } from "../queries/useStatementHistory";
import { knownPaymentKeys } from "../utils/paymentMatching";

export interface PaymentReceivedNotice {
  statementId: string | null;
  paidAt: number;
  knownPayments: ReadonlySet<string> | null;
}

const SEEN_FRAMES_LIMIT = 200;
const seenFrames = new Set<string>();

export function resetSeenPaymentFramesForTests() {
  seenFrames.clear();
}

function getPaidAt(occurredAtUtc?: string | null) {
  const time = occurredAtUtc ? Date.parse(occurredAtUtc) : Number.NaN;
  return Number.isNaN(time) ? Date.now() : time;
}

export function usePaymentReceivedNotice(patientId: string | null) {
  const { tenantId } = useTenantRouteContext();
  const queryClient = useQueryClient();
  const identity = `${tenantId}:${patientId}`;
  const [received, setReceived] = useState<{
    identity: string;
    statementId: string | null;
    paidAt: number;
    knownPayments: ReadonlySet<string> | null;
  } | null>(null);

  if (received && received.identity !== identity) setReceived(null);

  useSignalREvent<PatientArChangedEvent>(
    SIGNALR_EVENTS.PATIENT_AR_CHANGED,
    (event) => {
      if (
        event.kind !== "PaymentReceived" ||
        event.practiceId !== tenantId ||
        event.patientId !== patientId
      ) {
        return;
      }

      if (event.occurredAtUtc) {
        const frame = `${identity}|${event.statementId}|${event.occurredAtUtc}`;
        if (seenFrames.has(frame)) return;
        seenFrames.add(frame);
        if (seenFrames.size > SEEN_FRAMES_LIMIT) {
          seenFrames.delete(seenFrames.values().next().value as string);
        }
      }

      void queryClient.invalidateQueries(
        { queryKey: statementHistoryQueryKey(tenantId, event.patientId) },
        { cancelRefetch: false },
      );

      const history = queryClient.getQueryData<StatementHistoryItem[]>(
        statementHistoryQueryKey(tenantId, event.patientId),
      );

      setReceived({
        identity,
        statementId: event.statementId,
        paidAt: getPaidAt(event.occurredAtUtc),
        knownPayments: knownPaymentKeys(history, event.statementId),
      });
    },
  );

  const notice = useMemo<PaymentReceivedNotice | null>(
    () =>
      received?.identity === identity
        ? {
            statementId: received.statementId,
            paidAt: received.paidAt,
            knownPayments: received.knownPayments,
          }
        : null,
    [received, identity],
  );
  const dismiss = useCallback(() => setReceived(null), []);

  return { notice, dismiss };
}
